import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Check, Loader2, X, Sparkles, Trash2 } from "lucide-react";

interface AddonRow {
  id: string;
  name: string;
  price: number;
  duration: string;
  status: "pending" | "confirmed" | "declined";
  requested_by: "client" | "admin";
  created_at: string;
}

interface Props {
  bookingId: string;
  /** Re-fetch parent (booking price/duration) after a confirm */
  onChange?: () => void;
}

const statusStyles: Record<string, string> = {
  pending: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  confirmed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  declined: "bg-rose-500/10 text-rose-600 border-rose-500/30",
};

const AddonRequestsList = ({ bookingId, onChange }: Props) => {
  const { toast } = useToast();
  const [rows, setRows] = useState<AddonRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("booking_addons")
      .select("id, name, price, duration, status, requested_by, created_at")
      .eq("booking_id", bookingId)
      .order("created_at", { ascending: true });
    if (error) {
      toast({ title: "Failed to load add-ons", description: error.message, variant: "destructive" });
    } else {
      setRows((data || []) as AddonRow[]);
    }
    setLoading(false);
  }, [bookingId, toast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setStatus = async (id: string, next: "confirmed" | "declined") => {
    setBusyId(id);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("booking_addons")
      .update({
        status: next,
        confirmed_by: user?.id ?? null,
        confirmed_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      setBusyId(null);
      return;
    }

    // Notify
    try {
      await supabase.functions.invoke("notify-addon-request", {
        body: { booking_id: bookingId, addon_ids: [id], event: next },
      });
    } catch (e) { console.error("notify failed", e); }

    if (next === "confirmed") {
      try {
        const { data: booking } = await supabase
          .from("bookings")
          .select("id, service_name, service_category, booking_date, booking_time, duration, status, notes").is("deleted_at", null)
          .eq("id", bookingId)
          .maybeSingle();
        if (booking) {
          const { syncBookingToOutlook } = await import("@/lib/outlookSync");
          syncBookingToOutlook(booking, "updated").catch(() => {});
        }
      } catch (e) { console.error("calendar sync failed", e); }
    }

    toast({ title: next === "confirmed" ? "Confirmed" : "Declined" });
    setBusyId(null);
    await fetchRows();
    onChange?.();
  };

  const removeRow = async (id: string) => {
    setBusyId(id);
    const { error } = await supabase.from("booking_addons").delete().eq("id", id);
    setBusyId(null);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    fetchRows();
    onChange?.();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-3 text-muted-foreground">
        <Loader2 size={14} className="animate-spin" />
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
        <Sparkles size={10} className="text-primary" /> Add-ons
      </div>
      {rows.map((r) => (
        <div
          key={r.id}
          className="flex items-center justify-between gap-2 p-2 border border-border/40 bg-background/40 text-sm"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate">{r.name}</p>
              <Badge variant="outline" className={`text-[9px] tracking-widest uppercase ${statusStyles[r.status]}`}>
                {r.status}
              </Badge>
              {r.requested_by === "admin" && (
                <span className="text-[9px] tracking-widest uppercase text-muted-foreground">admin</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">+${Number(r.price).toFixed(2)} · {r.duration}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {r.status === "pending" && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-emerald-600 hover:text-emerald-700"
                  disabled={busyId === r.id}
                  onClick={() => setStatus(r.id, "confirmed")}
                >
                  {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={14} />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-rose-600 hover:text-rose-700"
                  disabled={busyId === r.id}
                  onClick={() => setStatus(r.id, "declined")}
                >
                  <X size={14} />
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-muted-foreground"
              disabled={busyId === r.id}
              onClick={() => removeRow(r.id)}
            >
              <Trash2 size={12} />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AddonRequestsList;
